import { useEffect, useMemo, useState } from "react";
import "./DesktopLogin.css";
import { authApi, setAuthToken } from "../../../api";
import { useLang } from "../../../i18n/LangContext";
import DesktopForgotPassword from "./DesktopForgotPassword";

const FALLBACK_COPY = {
  eyebrow: "LOGIN",
  title: "Access your account",
  subtitle: "Use your advertiser/publisher credentials to continue.",
  emailLabel: "Email",
  emailPlaceholder: "you@example.com",
  passwordLabel: "Password",
  passwordPlaceholder: "Enter your password",
  submit: "Log in",
  forgot: "Forgot password?",
  helper: "Need help? Reach out to our team.",
  success: "Logged in successfully.",
  missingFields: "Add email & password.",
  genericError: "Unable to log in. Please try again.",
};

export default function DesktopLogin({ open, onClose = () => {}, onSuccess = () => {}, onRequireVerification = () => {} }) {
  const { t } = useLang();
  const copy = useMemo(() => t.loginModal || FALLBACK_COPY, [t.loginModal]);

  const [values, setValues] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForgot, setShowForgot] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setValues({ email: "", password: "" });
    setError("");
    setSuccess("");
  }, [open]);

  if (!open) return null;

  const updateValue = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const payload = { email: values.email.trim(), password: values.password };

    if (!payload.email || !payload.password) {
      setError(copy.missingFields || FALLBACK_COPY.missingFields);
      return;
    }

    try {
      setLoading(true);
      const response = await authApi.login(payload);
      const token = response?.token ?? response?.result?.token ?? "";
      if (token) setAuthToken(token);
      const user = response?.user || response?.result?.user || null;
      setSuccess(copy.success || FALLBACK_COPY.success);
      onSuccess({ token, user, payload });
    } catch (err) {
      if (err?.data?.statusDesc === "email_not_verified") {
        onRequireVerification({ email: payload.email });
      }
      const message =
        err?.data?.statusText ||
        err?.data?.statusDesc ||
        err?.message ||
        copy.genericError ||
        FALLBACK_COPY.genericError;
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="desktop-login-overlay" role="dialog" aria-modal="true" onMouseDown={handleBackdrop}>
      <div className="desktop-login-card">
        <button
          type="button"
          className="desktop-login-close"
          aria-label="Close login"
          onClick={onClose}
        >
          ×
        </button>

        <p className="desktop-login-eyebrow">{copy.eyebrow}</p>
        <h2 className="desktop-login-title">{copy.title}</h2>
        <p className="desktop-login-subtitle">{copy.subtitle}</p>

        <form className="desktop-login-form" onSubmit={handleSubmit}>
          <label className="desktop-login-field">
            <span>{copy.emailLabel}</span>
            <input
              type="email"
              name="email"
              placeholder={copy.emailPlaceholder}
              value={values.email}
              onChange={updateValue}
              autoComplete="email"
            />
          </label>

          <label className="desktop-login-field">
            <span>{copy.passwordLabel}</span>
            <input
              type="password"
              name="password"
              placeholder={copy.passwordPlaceholder}
              value={values.password}
              onChange={updateValue}
              autoComplete="current-password"
            />
          </label>

          {(error || success) && (
            <div className={`desktop-login-status ${error ? "is-error" : "is-success"}`}>
              {error || success}
            </div>
          )}

          <button type="submit" className="desktop-login-submit" disabled={loading}>
            <span>{loading ? "..." : (copy.submit || FALLBACK_COPY.submit).toUpperCase()}</span>
          </button>
        </form>

        <div className="desktop-login-helper-row">
          <button type="button" className="desktop-login-link" onClick={() => setShowForgot(true)}>
            {copy.forgot || "Forgot password?"}
          </button>
          <p className="desktop-login-helper">{copy.helper}</p>
        </div>

        <DesktopForgotPassword open={showForgot} onClose={() => setShowForgot(false)} />
      </div>
    </div>
  );
}
